"use client";

import React, { useMemo } from "react";
import styles from "./premium-media-hub.module.css";
import { ListingData } from "@/types/types";
import { getMedia } from "@/lib/media/shared";
import {
  IoImagesOutline,
  IoPlayCircleOutline,
  IoCubeOutline,
  IoDocumentTextOutline,
  IoArrowForwardOutline,
  IoMapOutline,
} from "react-icons/io5";

interface PremiumMediaHubProps {
  listingData: ListingData;
  onOpenGallery?: () => void;
  onOpenVideo?: () => void;
  onOpenTour?: () => void;
  onOpenBrochure?: () => void;
  onOpenFloorPlans?: () => void;
}

export default function PremiumMediaHub({
  listingData,
  onOpenGallery,
  onOpenVideo,
  onOpenTour,
  onOpenBrochure,
  onOpenFloorPlans,
}: PremiumMediaHubProps) {
  // Safety check in case data is still loading or props were passed incorrectly
  if (!listingData) return null;

  // 1. Parse and safely categorize photos
  const { generalPhotos, floorPlans, totalPhotos } = useMemo(() => {
    const raw = Object.values(listingData?.photos || {});
    const plans = raw.filter((photo) => photo?.room_tag === "floor_plans");

    // Create a pool of general photos that ARE NOT floor plans for the background tiles
    const general = raw.filter((photo) => photo?.room_tag !== "floor_plans");

    return {
      generalPhotos: general.length > 0 ? general : raw,
      floorPlans: plans,
      totalPhotos: raw.length,
    };
  }, [listingData?.photos]);

  const getTileBackground = (index: number) => {
    const photo = generalPhotos[index] || generalPhotos[0];
    return photo ? getMedia(photo, "md") : null;
  };

  // Resolve the video safely
  const videoResolved = listingData?.presentation_video_data
    ? getMedia(listingData.presentation_video_data, "md")
    : null;

  const activeModules = [];

  // --- MODULE 1: Photos ---
  activeModules.push({
    id: "photos",
    title: "Galerie Foto",
    subtitle: `${totalPhotos} IMAGINI DISPONIBILE`,
    icon: <IoImagesOutline />,
    bgSrc: getTileBackground(0)?.src,
    bgWebp: getTileBackground(0)?.webp,
    onClick: onOpenGallery,
  });

  // --- MODULE 2: Video ---
  if (listingData?.has_presentation_video) {
    const hasRealPreview = !!videoResolved?.preview;
    const fallbackImage = getTileBackground(1);

    activeModules.push({
      id: "video",
      title: "Video Prezentare",
      subtitle: "TUR CINEMATIC",
      icon: <IoPlayCircleOutline />,
      bgSrc: hasRealPreview ? videoResolved.preview : fallbackImage?.src,
      bgWebp: hasRealPreview ? undefined : fallbackImage?.webp,
      onClick: onOpenVideo,
    });
  }

  // --- MODULE 3: 3D Tour (iFrame Injection) ---
  if (listingData?.has_web_tour && listingData?.web_tour_url) {
    activeModules.push({
      id: "tour",
      title: "Tur Virtual 3D",
      subtitle: "EXPLOREAZĂ",
      icon: <IoCubeOutline />,
      iframeUrl: listingData.web_tour_url,
      onClick: onOpenTour,
    });
  }

  // --- MODULE 4: Floor Plans ---
  if (floorPlans.length > 0) {
    const planBg = getMedia(floorPlans[0], "md");
    activeModules.push({
      id: "floor_plans",
      title: "Schițe & Planuri",
      subtitle: `${floorPlans.length} PLANURI DISPONIBILE`,
      icon: <IoMapOutline />,
      bgSrc: planBg?.src || getTileBackground(2)?.src,
      bgWebp: planBg?.webp || getTileBackground(2)?.webp,
      onClick: onOpenFloorPlans,
    });
  }

  // --- MODULE 5: Brochure ---
  if (listingData?.has_brochure) {
    activeModules.push({
      id: "brochure",
      title: "Broșură PDF",
      subtitle: "SPECIFICAȚII",
      icon: <IoDocumentTextOutline />,
      bgSrc: getTileBackground(3)?.src,
      bgWebp: getTileBackground(3)?.webp,
      onClick: onOpenBrochure,
    });
  }

  const moduleCount = activeModules.length;

  return (
    <section className={styles.hubSection}>
      <div className={styles.header}>
        <h2 className={styles.sectionTitle}>Explorează media</h2>
        <span className={styles.sectionSubtitle}>
          Descoperă proprietatea în detaliu prin experiențe vizuale imersive.
        </span>
      </div>

      <div
        className={`${styles.editorialGrid} ${styles[`layout-${moduleCount}`]}`}
      >
        {activeModules.map((mod) => (
          <button
            key={mod.id}
            className={styles.editorialTile}
            onClick={mod.onClick}
            aria-label={mod.title}
          >
            {/* IFRAME RENDER (For 3D Tours) */}
            {mod.iframeUrl ? (
              <div className={styles.iframeWrapper}>
                <iframe
                  className={styles.bgIframe}
                  src={mod.iframeUrl}
                  frameBorder="0"
                  allowFullScreen={true}
                  allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
                  tabIndex={-1}
                />
                <div className={styles.iframeClickShield} />
              </div>
            ) : (
              /* STANDARD IMAGE RENDER */
              mod.bgSrc && (
                <div className={styles.tileBackground}>
                  <picture style={{ display: "contents" }}>
                    {mod.bgWebp && (
                      <source srcSet={mod.bgWebp} type="image/webp" />
                    )}
                    <img
                      src={mod.bgSrc}
                      alt={mod.title}
                      loading="lazy"
                      decoding="async"
                      className={styles.bgImage}
                    />
                  </picture>
                </div>
              )
            )}

            <div className={styles.tileGradient} />

            <div className={styles.tileContent}>
              <div className={styles.iconWrapper}>{mod.icon}</div>
              <div className={styles.textWrapper}>
                <span className={styles.tileSubtitle}>{mod.subtitle}</span>
                <h3 className={styles.tileTitle}>{mod.title}</h3>
              </div>
              <div className={styles.hoverArrow}>
                <IoArrowForwardOutline />
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
